import IconButtonLink from "@/components/Link/IconButtonLink.js";
import { useQueryOptionsFactory } from "@/queries/index.js";
import ReviewCard from "@/routes/manage/reviews/-components/ReviewCard.js";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Alert, Divider, Stack } from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { fallback, zodValidator } from "@tanstack/zod-adapter";
import type { ReactNode } from "react";
import { z } from "zod";

const Root = (): ReactNode => {
    const qof = useQueryOptionsFactory();
    const { pageParams } = Route.useSearch();
    const page = useSuspenseQuery(qof.review.list({ pageParams })).data;

    if (page.data.length === 0) {
        return <Alert severity="info">No reviews submitted yet.</Alert>;
    }

    return (
        <>
            <Stack spacing={2}>
                {page.data.map((review) => (
                    <ReviewCard review={review} key={review.id} />
                ))}
            </Stack>

            {(page.pageParams.first || page.pageParams.next) && (
                <>
                    <Divider sx={{ mt: 2, mb: 1 }} />
                    <IconButtonLink
                        to="."
                        search={{ pageParams: page.pageParams.first }}
                        disabled={!page.pageParams.first}
                        onClick={() => {
                            window.scroll({ top: 0 });
                        }}
                    >
                        <FirstPageIcon />
                    </IconButtonLink>

                    <IconButtonLink
                        to="."
                        search={{ pageParams: page.pageParams.prev }}
                        disabled={!page.pageParams.prev}
                        onClick={() => {
                            window.scroll({ top: 0 });
                        }}
                    >
                        <KeyboardArrowLeftIcon />
                    </IconButtonLink>
                    <IconButtonLink
                        to="."
                        search={{ pageParams: page.pageParams.next }}
                        disabled={!page.pageParams.next}
                        onClick={() => {
                            window.scroll({ top: 0 });
                        }}
                    >
                        <KeyboardArrowRightIcon />
                    </IconButtonLink>
                </>
            )}
        </>
    );
};

const searchSchema = z.object({
    pageParams: fallback(z.record(z.string()), { size: "10" }).default({ size: "10" }),
});

export const Route = createFileRoute("/manage/reviews/")({
    component: Root,
    validateSearch: zodValidator(searchSchema),
    loaderDeps: ({ search: { pageParams } }) => ({ pageParams }),
    loader: async ({ context, deps }) => {
        await context.queryClient.ensureQueryData(
            context.qof.review.list({
                pageParams: deps.pageParams,
            }),
        );
    },
});
